import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Receipt, Plus, X, Check, ArrowRightLeft, Pin, ExternalLink, Crown, ShieldCheck, AtSign, Loader2,
  Sparkles, Star, ChevronRight, Mail, Send, Activity, Layers,
  LifeBuoy, Download, AlertTriangle, Search, Eye, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isAdmin } from "../lib/admin";
import { earnCoins } from "../lib/coins";
import { useTrackPageView } from "../hooks/useAnalytics";
import VaultIDCard from "../components/profile/VaultIDCard";
import StreakHeatmap from "../components/profile/StreakHeatmap";
import NextSigilHint from "../components/profile/NextSigilHint";
import InsightsRow from "../components/profile/InsightsRow";
import TrustScoreBadge from "../components/profile/TrustScoreBadge";
import PageHeader from "../components/PageHeader";
import ArtifactVaultCard from "../components/cards/ArtifactVaultCard";
import FeaturedSigil from "../components/profile/FeaturedSigil";
import PublicArtifacts from "../components/profile/PublicArtifacts";
import BioEditor from "../components/profile/BioEditor";
import TaglineEditor from "../components/profile/TaglineEditor";
// AccentColorPicker moved into Theme Studio (Accent + Text tab).
// import AccentColorPicker from "../components/profile/AccentColorPicker";
// AvatarPicker (legacy doc-card icon picker) removed — the AI avatar picker
// lives inside Theme Studio's first tab as the only avatar entry point.
// AIAvatarPicker modal removed — replaced by InlineAvatarPicker tab inside Theme Studio.
// AIAvatarMedia + BentoGrid imports removed — the Bento Layout + AI Avatar
// CTAs both live inside Theme Studio now (no duplicate surface on profile).
import PinnedSigilBadge from "../components/profile/PinnedSigilBadge";
import AnniversaryBanner from "../components/profile/AnniversaryBanner";
// FeaturedCardEditor removed — Favorite Vault Cards binder (up to 5)
// further down on the page replaces the single-card pin.
import MindChangedEditor from "../components/profile/MindChangedEditor";
import FavoriteCardsBinder from "../components/profile/FavoriteCardsBinder";
import DoxxCardSection from "../components/DoxxCardSection";
import AnonModePanel from "../components/profile/AnonModePanel";
import VaultRankLadder from "../components/profile/VaultRankLadder";
// FullSigilWall + SigilGrid moved to the dedicated /sigils page.
import ClassifiedFrame from "../components/profile/ClassifiedFrame";
import DailyChartCard from "../components/profile/DailyChartCard";
import BlockMuteManager from "../components/profile/BlockMuteManager";
import ActiveStatusToggle from "../components/profile/ActiveStatusToggle";
import ApplyTheme from "../components/themes/ApplyTheme";
import { useVaultTheme } from "../components/themes/VaultThemeProvider";
import { celebrateNewSigils } from "../lib/sigilCelebration";

// Mirrors the backend ACCENT_PALETTE — used only to resolve a color id → hex
// for banner / VaultIDCard styling without an extra round trip.
const ACCENT_HEX = {
  purple: "#a78bfa", cyan: "#22d3ee", fuchsia: "#ec4899", amber: "#ffd60a",
  lime: "#00ff88", red: "#ff3b30", orange: "#ff9500", blue: "#60a5fa",
};

// Pulls the signed-in user's saved theme from context and applies it
// across their own profile page so they can see it as visitors will.
function OwnThemeApplier() {
  const { myTheme } = useVaultTheme();
  if (!myTheme) return null;
  return <ApplyTheme theme={myTheme} ownerLabel="you" source="own-profile" />;
}

export default function ProfilePage() {
  useTrackPageView("/profile");
  const { user, ready } = useAuth();
  const [sigilsData, setSigilsData] = useState({ earned: [], catalog: [] });
  const [loyalty, setLoyalty] = useState({ top_categories: [], total_engagements: 0 });
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [trustScore, setTrustScore] = useState(null);
  const [streak, setStreak] = useState(null);
  const [publicProfile, setPublicProfile] = useState(null);
  // (AI avatar picker lives entirely in /theme-studio?tab=avatar now;
  // the legacy AvatarPicker modal has been retired from this page.)

  useEffect(() => {
    if (!ready || !user) return;
    // Trigger re-evaluation server-side AND celebrate any newly-earned sigils.
    celebrateNewSigils().catch(() => {});
    Promise.all([
      api.get("/me/sigils").then((r) => r.data).catch(() => null),
      api.get("/me/loyalty").then((r) => r.data).catch(() => null),
      api.get("/me/stats").then((r) => r.data).catch(() => null),
      api.get("/me/insights").then((r) => r.data).catch(() => null),
      api.get("/me/trust-score").then((r) => r.data).catch(() => null),
      api.get("/me/streak").then((r) => r.data).catch(() => null),
      api.get(`/users/${user.username}`).then((r) => r.data).catch(() => null),
    ]).then(([s, l, st, ins, ts, sk, pub]) => {
      if (s) setSigilsData(s);
      if (l) setLoyalty(l);
      if (st) setStats(st);
      if (ins) setInsights(ins);
      if (ts) setTrustScore(ts);
      if (sk) setStreak(sk);
      if (pub) setPublicProfile(pub);
    });
  }, [ready, user]);

  if (!ready) {
    return <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 font-mono text-xs text-zinc-500 tracking-[0.2em]">// LOADING…</div>;
  }

  if (!user) {
    return (
      <div data-testid="profile-signin">
        <PageHeader
          color="#a78bfa"
          label="Identity"
          icon={<Crown size={11} />}
          title={<>Your File in the <span className="text-[#a78bfa]">Vault.</span></>}
          subtitle={<>Sign in to track your <strong className="text-[#a78bfa]">sigils</strong>, <strong className="text-[#a78bfa]">loyalty</strong>, <strong className="text-[#a78bfa]">coin economy</strong>, and yearly receipts. No email or phone &mdash; just a username, a password, and a recovery code.</>}
          testid="profile-header-signed-out"
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-20 text-center">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("open-auth", { detail: { mode: "signup" } }))}
            data-testid="profile-signin-btn"
            className="inline-block px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] bg-[#a78bfa] text-[#050505] hover:bg-white transition-colors">SIGN IN / UP</button>
        </div>
      </div>
    );
  }

  const earnedCount = (sigilsData.earned || []).length;
  const now = new Date();

  return (
    <div data-testid="profile-page">
      <OwnThemeApplier />
      <PageHeader
        color={ACCENT_HEX[publicProfile?.accent_color] || "#a78bfa"}
        label={`Signed in as @${user.username}`}
        icon={<Crown size={11} />}
        title={<>Your File in the <span style={{ color: ACCENT_HEX[publicProfile?.accent_color] || "#a78bfa" }}>Vault.</span></>}
        subtitle={<>Your archivist HQ. Set a tagline, accent color, avatar, pinned sigils, and a pseudonym to post under different masks. Customize your <code className="font-mono text-zinc-300">/u/{user.username}</code> public Padfolio from here too.</>}
        testid="profile-header"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 space-y-12">

      {/* Admin badge intentionally removed 2026-02 — admin is decoupled from
          user accounts. Reach the admin tools via /admin URL + access code. */}
      {/* Anniversary banner — auto-shows in milestone window */}
      <AnniversaryBanner
        anniversary={publicProfile?.anniversary}
        accentHex={publicProfile?.accent_color_hex || ACCENT_HEX[publicProfile?.accent_color] || "#a78bfa"}
      />

      {/* Vault ID Card — top hero */}
      <section data-testid="profile-id-section">
        <VaultIDCard
          username={user.username}
          joinedAt={publicProfile?.joined_at || stats?.joined_at}
          rank={insights?.vault_rank}
          sigilCount={earnedCount}
          trustScore={trustScore?.score ?? null}
          avatarId={user.avatar_id || publicProfile?.avatar_id || null}
          aiAvatarId={user.ai_avatar_id || publicProfile?.ai_avatar_id || null}
          accentHex={publicProfile?.accent_color_hex || ACCENT_HEX[publicProfile?.accent_color] || null}
          bio={publicProfile?.bio}
          tagline={publicProfile?.tagline}
          memberNumber={publicProfile?.member_number ?? user?.member_number ?? null}
          exportable
        />
        <BioEditor
          value={publicProfile?.bio}
          onSaved={(newBio) => setPublicProfile((p) => ({ ...(p || {}), bio: newBio }))}
        />
        <TaglineEditor
          value={publicProfile?.tagline}
          onSaved={(t) => setPublicProfile((p) => ({ ...(p || {}), tagline: t }))}
        />
        {/* Anon Mode lives further down the page now (between Favorite
            Cards and the Privacy panel) — it fits better in the
            "preference toggles" cluster than at the top of the hero. */}
        {/* 7-day Daily Rewards Chart — forgiving daily-claim ladder that
            walks all 7 rarity tiers (Common → Celestial). Lives in the
            hero because it's the highest-frequency reward surface. */}
        <DailyChartCard />
        {/* Public link */}
        <Link
          to={`/u/${user.username}`}
          data-testid="profile-public-link"
          className="inline-flex items-center gap-1.5 mt-3 font-mono text-[10px] tracking-[0.25em] text-zinc-500 hover:text-[#a78bfa] transition-colors"
        >
          <ExternalLink size={11} /> SEE YOUR PUBLIC PROFILE — /u/{user.username}
        </Link>
      </section>

      {/* Studios + Daily Rituals + Mailbox — 3×2 grid below the hero. Each
          card stays brand-distinct via accent color, no two share a hue.
          Top row: creative studios (Theme + Meme + Cards' style entries).
          Bottom row: daily rituals + private log + mailbox. */}
      <section data-testid="profile-studios-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StudioCard
          accent="#7c3aed"
          label="THEME STUDIO"
          title="Customize your Vault"
          blurb="Background, avatar, accent color, cursor, sound, Padfolio."
          href="/theme-studio"
          cta="Open"
          testid="profile-theme-studio-cta"
          icon={<Sparkles size={13} />}
        />
        <StudioCard
          accent="#ec4899"
          label="MEME STUDIO"
          title="Make a Meme"
          blurb="Avatars, GIFs, your photos. Save, publish, earn coins."
          href="/meme-studio"
          cta="Open"
          testid="profile-meme-studio-cta"
          icon={<Sparkles size={13} />}
        />
        <StudioCard
          accent="#fde047"
          label="SOUL STATIC"
          title="Today's Transmissions"
          blurb="Tarot, fortune, coin toss, chart reading, dream decoder. Tune in daily."
          href="/soul-static"
          cta="Tune in"
          testid="profile-tarot-cta"
          icon={<Sparkles size={13} />}
        />
        <StudioCard
          accent="#22d3ee"
          label="VAULT POSTCARDS"
          title="Send a Postcard"
          blurb="Pick a face + a place + a message. 5 free per day."
          href="/postcards"
          cta="Open"
          testid="profile-postcards-cta"
          icon={<Send size={13} />}
        />
        <StudioCard
          accent="#a78bfa"
          label="ASCENSION TRACKER"
          title="Today's Symptom Log"
          blurb="Private daily check-in. 30-day streak unlocks a Celestial card."
          href="/ascension"
          cta="Open"
          testid="profile-ascension-cta"
          icon={<Activity size={13} />}
        />
        <StudioCard
          accent="#10b981"
          label="VAULT MAILBOX"
          title="Your Messages"
          blurb="DMs, burner threads, postcards in. The only inbox on the site."
          href="/messages"
          cta="Open"
          testid="profile-messages-cta"
          icon={<Mail size={13} />}
        />
      </section>

      {/* Sigil Library CTA + Earned This Week — moved ABOVE the pinned
          sigil row so the entry to the full library shows up before the
          pinned-slot preview. Stretched button row matches Theme Studio
          aesthetic; full sigil wall + grid live on /sigils. */}
      <section
        data-testid="profile-sigils-cta"
        className="border border-[#a78bfa]/40 p-5 sm:p-6 flex items-center gap-4 flex-wrap"
        style={{ background: "linear-gradient(to right, #1a0a2e, #2a1a3e)" }}
      >
        <div className="flex-1 min-w-[260px]">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#a78bfa] mb-1">
            // SIGIL LIBRARY · {earnedCount} OF {(sigilsData.catalog || []).length} EARNED
          </div>
          <h3 className="font-heading uppercase tracking-tight text-white text-xl sm:text-2xl mb-1">Your Sigil Library</h3>
          <p className="text-zinc-400 text-sm">
            Browse the {(sigilsData.catalog || []).length}-sigil catalog and pin up to 3 to your public profile.
            Locked sigils stay redacted until you earn them in the Vault.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 w-full sm:w-auto">
          <Link
            to="/sigils?tab=library"
            data-testid="profile-sigils-library-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#a78bfa] text-black hover:bg-[#c4b1ff] font-mono text-[11px] tracking-[0.25em] uppercase "
          >
            <Crown size={13} /> My Library · {earnedCount}
          </Link>
          <Link
            to="/sigils?tab=catalog"
            data-testid="profile-sigils-catalog-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#a78bfa]/60 text-[#a78bfa] hover:bg-[#a78bfa]/10 font-mono text-[11px] tracking-[0.25em] uppercase "
          >
            <Sparkles size={13} /> Full Catalog · {(sigilsData.catalog || []).length}
          </Link>
        </div>
      </section>

      {/* Earned This Week — only renders if a sigil was earned within 7 days */}
      {insights?.featured_sigil && <FeaturedSigil sigil={insights.featured_sigil} />}

      {/* Pinned sigil + Doxx Card — a paired row at the top of the
          customization area. Each half is responsive: pinned sigil sits
          alongside the user's permanent Classified Doxx Card. */}
      <section className="grid md:grid-cols-2 gap-4" data-testid="profile-identity-row">
        {(publicProfile?.pinned_sigils?.length > 0 || publicProfile?.pinned_sigil) ? (
          <PinnedSigilBadge
            sigils={publicProfile?.pinned_sigils}
            sigil={publicProfile?.pinned_sigil}
          />
        ) : (
          <div
            className="border border-[#a78bfa]/40 p-5 text-center flex flex-col items-center justify-center"
            style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.10), rgba(167,139,250,0.02)), #0a0a0a" }}
          >
            <Crown size={20} className="text-[#a78bfa]/70 mb-2" />
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#a78bfa] mb-1">// NO PINNED SIGIL</div>
            <p className="text-zinc-300 text-sm">Earn one by exploring the Vault.</p>
          </div>
        )}
        <DoxxCardSection
          compact
          onAvatarChanged={(id) => setPublicProfile((p) => ({ ...(p || {}), avatar_id: id }))}
        />
      </section>

      {/* Customization row: mind-changed only. The Featured Vault Card
          editor was removed — the Favorite Vault Cards binder (up to 5)
          further down replaces it. */}
      <section className="grid md:grid-cols-1 gap-4" data-testid="profile-customization">
        <MindChangedEditor
          mindChanged={publicProfile?.mind_changed}
          onSaved={(slug) => {
            if (slug === null) {
              setPublicProfile((p) => ({ ...(p || {}), mind_changed: null }));
            } else {
              api.get(`/users/${user.username}`)
                .then((r) => setPublicProfile((p) => ({ ...(p || {}), mind_changed: r.data?.mind_changed })))
                .catch(() => {});
            }
          }}
        />
      </section>

      {/* Theme Studio CTA moved to top of profile (above Vault ID). */}

      {/* AI Avatar picker lives inside Theme Studio (first tab). The
          legacy doc-card AvatarPicker modal was retired from this page
          along with the "Change Avatar" button on the Vault ID card. */}


      {/* Trust score (private) */}
      <TrustScoreBadge data={trustScore} />

      {/* Streak heatmap + Next sigil hint */}
      <div className="grid md:grid-cols-2 gap-3">
        <StreakHeatmap
          days={streak?.days || []}
          currentStreak={streak?.current_streak || 0}
          totalActive={streak?.total_active_days || 0}
        />
        <NextSigilHint data={insights?.next_sigil} />
      </div>

      {/* Trajectory + Time-of-day */}
      <InsightsRow trajectory={insights?.trajectory} timeOfDay={insights?.time_of_day} />

      {/* Loyalty */}
      <section data-testid="profile-loyalty">
        <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa] mb-3">
          // TOPIC LOYALTY — WHAT YOU GO DEEP ON
        </div>
        {(loyalty.top_categories || []).length === 0 ? (
          <div
            className="border border-[#a78bfa]/40 p-6 font-mono text-xs tracking-[0.2em] text-[#a78bfa]/70 text-center"
            style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(167,139,250,0.02)), #0a0a0a" }}
          >
            // EXPLORE A FEW DOSSIERS TO BUILD YOUR LOYALTY PROFILE
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-3">
            {loyalty.top_categories.map((c, i) => (
              <div key={c.category} className="border border-[#a78bfa]/30 bg-[#0a0a0a] p-4">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa]">
                  #{i + 1} · {c.count} interactions
                </div>
                <div className="font-heading uppercase tracking-tight text-white text-lg mt-2 truncate">
                  {c.category.replaceAll("_", " ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Favorite Vault Cards — public binder, owner-editable */}
      <ClassifiedFrame
        label="// FAVORITE CARDS · UP TO 5"
        accent="#ff9500"
        icon={Star}
        testid="profile-fav-cards"
        className="mb-4"
      >
        <FavoriteCardsBinder
          cards={publicProfile?.favorite_cards || []}
          editable
          onChanged={(ids) => setPublicProfile((p) => ({ ...(p || {}), favorite_card_ids: ids }))}
        />
      </ClassifiedFrame>

      {/* Anon Mode — moved here from the hero. Sits between Favorite Cards
          and the Privacy/Blocks panel where it pairs naturally with the
          other "control how you appear to others" toggles. */}
      <ClassifiedFrame
        label="// GO OFF THE RECORD"
        accent="#a78bfa"
        icon={ShieldCheck}
        testid="profile-anon-mode-frame"
        className="mb-4"
      >
        <AnonModePanel />
      </ClassifiedFrame>

      {/* Block + Mute manager — visible inside an expandable panel */}
      <ClassifiedFrame
        label="// PRIVACY · BLOCKS & MUTES"
        accent="#ef4444"
        icon={ShieldCheck}
        testid="profile-blocks"
        className="mb-4"
      >
        <div className="space-y-3">
          <ActiveStatusToggle />
          <BlockMuteManager />
        </div>
      </ClassifiedFrame>

      {/* VAULT RANK PROGRESSION — sigil wall + sigil grid moved to /sigils.
          The rank ladder still lives here because it pairs naturally with
          the profile's identity stack (handle, joined date, vault ID). */}
      <ClassifiedFrame
        label="// VAULT RANK · PROGRESSION"
        accent={ACCENT_HEX[publicProfile?.accent_color] || "#a78bfa"}
        icon={ShieldCheck}
        testid="profile-rank-ladder"
      >
        <VaultRankLadder
          currentTitle={insights?.vault_rank?.title}
          sigilCount={earnedCount}
        />
      </ClassifiedFrame>

      {/* Personas */}
      <PersonaPanel />

      {/* Pinned / latest artifacts (with pin selectors) */}
      <PinnableArtifacts
        publicProfile={publicProfile}
        onPinChange={(updated) => setPublicProfile((p) => ({ ...(p || {}), ...updated }))}
      />

      {/* Receipts */}
      <section data-testid="profile-receipts">
        <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa] mb-3">
          // RECEIPTS — SHAREABLE YEAR / MONTH RECAPS
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to={`/r/${user.username}/${now.getFullYear()}`}
            data-testid="receipt-annual-link"
            className="border border-[#a78bfa]/30 bg-[#0a0a0a] p-5 hover:bg-[#0a0a0a] transition-colors group">
            <Receipt size={22} className="text-[#a78bfa] mb-2" />
            <div className="font-heading uppercase tracking-tight text-white text-lg">Year {now.getFullYear()}</div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mt-2 group-hover:text-[#a78bfa]">VIEW + SHARE →</div>
          </Link>
          <Link to={`/r/${user.username}/${now.getFullYear()}/${now.getMonth() + 1}`}
            data-testid="receipt-monthly-link"
            className="border border-[#a78bfa]/30 bg-[#0a0a0a] p-5 hover:bg-[#0a0a0a] transition-colors group">
            <Receipt size={22} className="text-[#a78bfa] mb-2" />
            <div className="font-heading uppercase tracking-tight text-white text-lg">
              {now.toLocaleString("en-US", { month: "long" }).toUpperCase()} {now.getFullYear()}
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mt-2 group-hover:text-[#a78bfa]">VIEW + SHARE →</div>
          </Link>
        </div>
      </section>

      {/* Vault Cards strip — quick stats + jump to collection room */}
      <MyVaultCardsStrip username={user.username} />
      {/* Artifact Cards strip — personal mints from dossiers, dives, connections */}
      <MyArtifactCardsStrip />

      {/* Account settings — change username (1/day) */}
      <AccountSettings />
      </div>
    </div>
  );
}

function AccountSettings() {
  const { user, changeUsername } = useAuth();
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!newName.trim() || !pw) return;
    try {
      setBusy(true);
      await changeUsername(newName.trim(), pw);
      toast.success(`Renamed to @${newName.trim()}`);
      setOpen(false);
      setNewName(""); setPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change username");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="profile-account-settings"
             className="border border-[#22d3ee]/40"
             style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.08), rgba(34,211,238,0.02)), #0a0a0a" }}>
      <div className="px-4 py-3 border-b border-[#22d3ee]/25 flex items-center gap-2">
        <AtSign size={14} className="text-[#22d3ee]" />
        <div className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee]">// ACCOUNT SETTINGS</div>
      </div>
      <div className="p-4 sm:p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Current handle</div>
            <div className="font-heading uppercase tracking-tight text-white text-xl mt-1">@{user.username}</div>
          </div>
          {!open && (
            <button type="button"
                    data-testid="profile-rename-btn"
                    onClick={() => setOpen(true)}
                    className="px-3 py-2 border border-[#22d3ee]/50 text-[#22d3ee] hover:bg-[#22d3ee]/10 font-mono text-[10px] tracking-[0.25em] uppercase">
              Change Username
            </button>
          )}
        </div>
        {open && (
          <form onSubmit={submit} className="space-y-3" data-testid="profile-rename-form">
            <p className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 leading-relaxed">
              Pick a new handle (3–30 chars, letters / numbers / underscores). You can change once every 24 hours. Your profile URL, threads, comments, and Vault Cards all follow your new handle automatically.
            </p>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="profile-rename-input"
              placeholder="new_handle"
              maxLength={30}
              autoFocus
              className="w-full bg-[#050505] border border-white/15 focus:border-[#22d3ee] outline-none px-3 py-2 font-mono text-xs text-white"
            />
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="profile-rename-pw"
              placeholder="Current password (for safety)"
              autoComplete="current-password"
              className="w-full bg-[#050505] border border-white/15 focus:border-[#22d3ee] outline-none px-3 py-2 font-mono text-xs text-white"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy || !newName.trim() || !pw}
                data-testid="profile-rename-submit"
                className="px-3 py-2 bg-[#22d3ee] text-[#050505] hover:bg-white font-mono text-[10px] tracking-[0.25em] uppercase disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                Save
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); setNewName(""); setPw(""); }}
                disabled={busy}
                data-testid="profile-rename-cancel"
                className="px-3 py-2 border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 font-mono text-[10px] tracking-[0.25em] uppercase"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function MyArtifactCardsStrip() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get("/artifact-cards/me")
      .then((r) => { if (!cancel) setItems(r.data?.items || []); })
      .catch(() => { if (!cancel) setItems([]); });
    return () => { cancel = true; };
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <section
      className="border border-[#ec4899]/40 mt-8"
      style={{ background: "linear-gradient(135deg, rgba(236,72,153,0.08), rgba(236,72,153,0.02)), #0a0a0a" }}
      data-testid="artifact-cards-strip">
      <header className="px-4 py-2.5 border-b border-[#ec4899]/25 flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#ec4899]">
          // ARTIFACT CARDS · {items.length}
        </span>
        <span className="font-mono text-[10px] text-[#ec4899]/70 tracking-wider">
          Minted from files you've explored
        </span>
      </header>
      <div className="p-4 flex flex-wrap gap-4">
        {items.slice(0, 12).map((c) => (
          <ArtifactVaultCard key={c.id} card={c} size="md" testid={`artifact-card-${c.id}`} />
        ))}
      </div>
    </section>
  );
}

function MyVaultCardsStrip({ username }) {
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState(null);
  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/vault-cards/me").then((r) => r.data).catch(() => null),
      api.get("/vault-cards/daily").then((r) => r.data).catch(() => null),
    ]).then(([mine, d]) => { if (!cancel) { setData(mine); setDaily(d); } });
    return () => { cancel = true; };
  }, []);

  if (!data) return null;
  const claimable = daily && !daily.claimed;
  return (
    <section data-testid="profile-vault-cards-strip">
      <div className="font-mono text-[10px] tracking-[0.25em] text-[#ff9500] mb-3 flex items-center gap-2">
        // VAULT CARDS
      </div>
      <Link
        to="/vault-cards"
        className={`block border p-5 transition-colors ${
          claimable
            ? "border-[#ff9500] bg-gradient-to-br from-[#ff9500]/[0.18] to-[#ff9500]/[0.04] hover:from-[#ff9500]/[0.25] hover:to-[#ff9500]/[0.08]"
            : "border-[#ff9500]/40 bg-gradient-to-br from-[#ff9500]/[0.08] to-[#ff9500]/[0.02] hover:from-[#ff9500]/[0.12] hover:to-[#ff9500]/[0.04]"
        }`}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading text-2xl text-white">
              {data.unique_count}<span className="text-[#ff9500]/60 text-base"> UNIQUE</span>
              <span className="text-[#ff9500]/60 text-base mx-2">·</span>
              {data.total_copies}<span className="text-[#ff9500]/60 text-base"> COPIES</span>
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-[#ff9500]/80 mt-1">
              {claimable ? "TODAY'S PULL IS WAITING →" : daily?.claimed ? "// PULLED TODAY · NEXT AT UTC MIDNIGHT" : "// VIEW COLLECTION →"}
            </div>
          </div>
          {claimable && (
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase bg-[#ff9500] text-[#050505] px-3 py-2 animate-pulse">
              Claim Now
            </div>
          )}
        </div>
      </Link>
    </section>
  );
}

// =============== Pinnable artifacts ===============
// Renders the user's latest dives + connections and lets them set a pinned one
// (one pinned dive + one pinned connection allowed). Public view shows pinned
// at the top of /u/:username.
function PinnableArtifacts({ publicProfile, onPinChange }) {
  if (!publicProfile) return null;
  const dives = publicProfile.latest_dives || [];
  const conns = publicProfile.latest_connections || [];
  const pinnedDiveId = publicProfile.pinned?.dive?.id;
  const pinnedConnId = publicProfile.pinned?.connection?.id;

  const togglePinDive = async (id) => {
    const next = pinnedDiveId === id ? "" : id;
    try {
      await api.put("/me/profile", { pinned_dive_id: next });
      onPinChange({
        pinned: {
          ...(publicProfile.pinned || {}),
          dive: next ? dives.find((d) => d.id === id) : null,
        },
      });
      toast.success(next ? "Pinned to profile" : "Unpinned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't pin");
    }
  };
  const togglePinConn = async (id) => {
    const next = pinnedConnId === id ? "" : id;
    try {
      await api.put("/me/profile", { pinned_connection_id: next });
      onPinChange({
        pinned: {
          ...(publicProfile.pinned || {}),
          connection: next ? conns.find((c) => c.id === id) : null,
        },
      });
      toast.success(next ? "Pinned to profile" : "Unpinned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't pin");
    }
  };

  if (dives.length === 0 && conns.length === 0) {
    return (
      <section data-testid="profile-artifacts-empty" className="space-y-8">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa] mb-3">// YOUR ARTIFACTS</div>
          <div
            className="border border-[#a78bfa]/40 p-6 font-mono text-xs tracking-[0.2em] text-[#a78bfa]/70 text-center"
            style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.10), rgba(167,139,250,0.02)), #0a0a0a" }}
          >
            // RUN A DEEP DIVE OR CONNECT-THE-DOTS — IT&apos;LL SHOW UP HERE
          </div>
        </div>
        <PinnableTiesBoards
          pinnedId={publicProfile.pinned?.warroom?.id}
          onChange={(w) => onPinChange({
            pinned: { ...(publicProfile.pinned || {}), warroom: w || null },
          })}
        />
      </section>
    );
  }

  return (
    <section data-testid="profile-artifacts" className="space-y-8">
      {dives.length > 0 && (
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa] mb-3 flex items-center justify-between">
            <span>// YOUR DEEP DIVES</span>
            <span className="text-zinc-500">Pin one for your public profile</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {dives.map((d) => (
              <div key={d.id} className={`border bg-[#0a0a0a] p-4 relative group transition-colors ${pinnedDiveId === d.id ? "border-[#a78bfa]/70 bg-[#0a0a0a]" : "border-white/10 hover:border-white/30"}`}>
                <button
                  type="button"
                  onClick={() => togglePinDive(d.id)}
                  data-testid={`pin-dive-${d.id}`}
                  title={pinnedDiveId === d.id ? "Unpin" : "Pin to profile"}
                  className={`absolute top-2 right-2 p-1 transition-colors ${pinnedDiveId === d.id ? "text-[#a78bfa]" : "text-zinc-500 hover:text-[#a78bfa] opacity-0 group-hover:opacity-100"}`}
                >
                  <Pin size={12} fill={pinnedDiveId === d.id ? "currentColor" : "none"} />
                </button>
                <Link to={`/ai-dossier/${d.id}`} className="block">
                  <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500">DEEP DIVE</div>
                  <div className="font-heading text-white text-sm sm:text-base mt-1 line-clamp-2 hover:text-[#a78bfa] transition-colors">
                    {d.topic}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
      {conns.length > 0 && (
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa] mb-3 flex items-center justify-between">
            <span>// YOUR CONNECTION MAPS</span>
            <span className="text-zinc-500">Pin one for your public profile</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {conns.map((c) => (
              <div key={c.id} className={`border bg-[#0a0a0a] p-4 relative group transition-colors ${pinnedConnId === c.id ? "border-[#a78bfa]/70 bg-[#0a0a0a]" : "border-white/10 hover:border-white/30"}`}>
                <button
                  type="button"
                  onClick={() => togglePinConn(c.id)}
                  data-testid={`pin-conn-${c.id}`}
                  title={pinnedConnId === c.id ? "Unpin" : "Pin to profile"}
                  className={`absolute top-2 right-2 p-1 transition-colors ${pinnedConnId === c.id ? "text-[#a78bfa]" : "text-zinc-500 hover:text-[#a78bfa] opacity-0 group-hover:opacity-100"}`}
                >
                  <Pin size={12} fill={pinnedConnId === c.id ? "currentColor" : "none"} />
                </button>
                <Link to={`/connection/${c.id}`} className="block">
                  <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500">CONNECTION</div>
                  <div className="font-heading text-white text-sm sm:text-base mt-1 line-clamp-2 hover:text-[#a78bfa] transition-colors">
                    {c.title}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
      <PinnableTiesBoards
        pinnedId={publicProfile.pinned?.warroom?.id}
        onChange={(w) => onPinChange({
          pinned: { ...(publicProfile.pinned || {}), warroom: w || null },
        })}
      />
    </section>
  );
}

// =============== Pinnable Ties boards ===============
// Lets the user select one of their own Ties boards to feature on /u/their-name.
// Loads on mount via /api/warroom/list/mine.
function PinnableTiesBoards({ pinnedId, onChange }) {
  const [rooms, setRooms] = useState(null);  // null = loading, [] = no rooms
  useEffect(() => {
    let cancelled = false;
    api.get("/warroom/list/mine")
      .then((r) => { if (!cancelled) setRooms(r.data?.items || []); })
      .catch(() => { if (!cancelled) setRooms([]); });
    return () => { cancelled = true; };
  }, []);

  const togglePin = async (room) => {
    const next = pinnedId === room.id ? "" : room.id;
    try {
      await api.put("/me/profile", { pinned_warroom_id: next });
      onChange(next ? room : null);
      toast.success(next ? "Board pinned to profile" : "Unpinned");
      // Coin reward — only when pinning (not unpinning). Daily cap of 30.
      if (next) {
        earnCoins("ties_board_pin", room.id).then((r) => {
          if (r?.awarded > 0) window.dispatchEvent(new Event("coins-changed"));
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't pin");
    }
  };

  if (rooms === null) return null;  // still loading — silent
  if (rooms.length === 0) {
    return (
      <div>
        <div className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee] mb-3 flex items-center justify-between">
          <span>// YOUR TIES BOARDS</span>
          <span className="text-zinc-500">Create one to pin it here</span>
        </div>
        <Link
          to="/warroom"
          data-testid="profile-create-warroom"
          className="block border border-dashed border-[#22d3ee]/40 p-6 font-mono text-xs tracking-[0.2em] text-[#22d3ee]/70 hover:text-[#22d3ee] hover:border-[#22d3ee]/60 text-center transition-colors"
          style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.08), rgba(34,211,238,0.02)), #0a0a0a" }}
        >
          // BUILD A PIN &amp; STRING BOARD →
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="pinnable-warrooms">
      <div className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee] mb-3 flex items-center justify-between">
        <span>// YOUR TIES BOARDS</span>
        <span className="text-zinc-500">Pin one to feature on your public profile</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {rooms.map((r) => {
          const isPinned = pinnedId === r.id;
          return (
            <div
              key={r.id}
              data-testid={`my-warroom-${r.id}`}
              className={`border p-4 relative group transition-colors bg-[#0a0a0a] ${
                isPinned
                  ? "border-[#22d3ee]/70"
                  : "border-[#22d3ee]/30 hover:border-[#22d3ee]/60"
              }`}
            >
              <button
                type="button"
                onClick={() => togglePin(r)}
                data-testid={`pin-warroom-${r.id}`}
                title={isPinned ? "Unpin" : "Pin to profile"}
                className={`absolute top-2 right-2 p-1 transition-colors ${
                  isPinned ? "text-[#22d3ee]" : "text-zinc-500 hover:text-[#22d3ee] opacity-0 group-hover:opacity-100"
                }`}
              >
                <Pin size={13} className={isPinned ? "fill-[#22d3ee]" : ""} />
              </button>
              <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 mb-1">
                {r.access === "public" ? "PUBLIC" : "INVITE-ONLY"} · {r.node_count} NODES · {r.edge_count} TIES
              </div>
              <Link to={`/warroom/${r.id}`}>
                <div className="font-heading text-white text-sm sm:text-base mt-1 line-clamp-2 hover:text-[#22d3ee] transition-colors">
                  {r.title}
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============== Persona switcher panel (unchanged from previous iteration) ===============
function PersonaPanel() {
  const [data, setData] = useState({ items: [], active_persona_id: null, real_username: "" });
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");

  const reload = () => api.get("/me/personas").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    if (name.trim().length < 2) { toast.error("Name too short"); return; }
    try {
      await api.post("/me/personas", { name: name.trim(), bio: bio.trim() || null });
      setName(""); setBio(""); setShowNew(false);
      toast.success("Persona created");
      reload();
      // Creating personas unlocks pseudonym / doppelganger sigils
      celebrateNewSigils().catch(() => {});
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
  };

  const switchTo = async (pid) => {
    try {
      await api.post("/me/personas/switch", { persona_id: pid });
      toast.success(pid ? "Persona active" : "Reverted to real username");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (pid) => {
    if (!window.confirm("Delete this persona? Past content remains attributed to it.")) return;
    try { await api.delete(`/me/personas/${pid}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const realActive = !data.active_persona_id;

  return (
    <section data-testid="profile-personas">
      <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa] mb-3 flex items-center justify-between">
        <span>// PSEUDONYM LAYERS — UP TO 3 PERSONAS</span>
        <span className="text-zinc-500">Switch identity per topic</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button onClick={() => switchTo(null)} data-testid="persona-real"
          className={`text-left p-4 border transition-colors ${realActive ? "border-[#a78bfa]/70" : "border-[#a78bfa]/30 hover:border-[#a78bfa]/50"}`}
          style={{ background: realActive
            ? "linear-gradient(135deg, rgba(167,139,250,0.14), rgba(167,139,250,0.02)), #0a0a0a"
            : "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(167,139,250,0.01)), #0a0a0a" }}>          <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500">REAL · @{data.real_username}</div>
          <div className="font-heading text-white text-lg mt-2 truncate">{data.real_username}</div>
          {realActive && <div className="font-mono text-[9px] tracking-[0.25em] text-[#a78bfa] mt-2 flex items-center gap-1"><Check size={10} /> ACTIVE</div>}
        </button>
        {data.items.map((p) => {
          const active = p.id === data.active_persona_id;
          return (
            <div key={p.id} data-testid={`persona-${p.id}`}
              className={`p-4 border group transition-colors ${active ? "border-[#a78bfa]/70" : "border-[#a78bfa]/30 hover:border-[#a78bfa]/50"}`}
              style={{ background: active
                ? "linear-gradient(135deg, rgba(167,139,250,0.14), rgba(167,139,250,0.02)), #0a0a0a"
                : "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(167,139,250,0.01)), #0a0a0a" }}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500">PERSONA</div>
                <button onClick={() => del(p.id)} title="Delete" className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[#ff3b30]">
                  <X size={11} />
                </button>
              </div>
              <div className="font-heading text-white text-lg mt-2 truncate">{p.name}</div>
              {p.bio && <p className="text-zinc-400 text-xs mt-1 line-clamp-2">{p.bio}</p>}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {active ? (
                  <span className="font-mono text-[9px] tracking-[0.25em] text-[#a78bfa] flex items-center gap-1"><Check size={10} /> ACTIVE</span>
                ) : (
                  <button onClick={() => switchTo(p.id)}
                    className="font-mono text-[9px] tracking-[0.25em] text-[#a78bfa] hover:text-white flex items-center gap-1">
                    <ArrowRightLeft size={10} /> SWITCH
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {data.items.length < 3 && (
          showNew ? (
            <form
              onSubmit={create}
              className="p-4 border border-[#a78bfa]/40 flex flex-col gap-2"
              style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.10), rgba(167,139,250,0.02)), #0a0a0a" }}
            >
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={40}
                data-testid="persona-name-input" placeholder="Persona name"
                className="bg-[#050505] border border-white/15 focus:border-[#a78bfa] outline-none px-2 py-1.5 font-mono text-xs text-white" />
              <input value={bio} onChange={(e) => setBio(e.target.value)} maxLength={240}
                placeholder="One-liner bio (optional)"
                className="bg-[#050505] border border-white/15 focus:border-[#a78bfa] outline-none px-2 py-1.5 font-mono text-xs text-white" />
              <div className="flex gap-1">
                <button type="submit" data-testid="persona-create-btn"
                  className="flex-1 px-2 py-1 bg-[#a78bfa] text-[#050505] font-mono text-[10px] tracking-[0.2em] hover:bg-white transition-colors">CREATE</button>
                <button type="button" onClick={() => { setShowNew(false); setName(""); setBio(""); }}
                  className="px-2 py-1 border border-white/10 text-zinc-400 font-mono text-[10px] tracking-[0.2em] hover:text-white transition-colors">X</button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowNew(true)}
              data-testid="persona-add-btn"
              className="p-4 border border-dashed border-[#a78bfa]/40 text-[#a78bfa]/70 hover:border-[#a78bfa]/70 hover:text-[#a78bfa] transition-colors flex flex-col items-center justify-center gap-1"
              style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(167,139,250,0.01)), #0a0a0a" }}
            >
              <Plus size={20} />
              <span className="font-mono text-[10px] tracking-[0.2em]">NEW PERSONA</span>
            </button>
          )
        )}
      </div>
    </section>
  );
}

function StudioCard({ accent, label, title, blurb, href, cta, testid, icon }) {
  return (
    <Link
      to={href}
      data-testid={testid}
      className="group relative block border bg-[#0a0a0a] p-4 sm:p-5 overflow-hidden hover:bg-[#0f0f0f] transition-colors"
      style={{ borderColor: `${accent}80` }}
    >
      {/* Top accent stripe */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ backgroundColor: accent }} />
      <div className="font-mono text-[9px] tracking-[0.3em] mb-1" style={{ color: accent }}>
        // {label}
      </div>
      <h3 className="font-heading uppercase tracking-tight text-white text-base sm:text-lg mb-1.5">{title}</h3>
      <p className="text-zinc-400 text-xs sm:text-[13px] mb-3 leading-relaxed">{blurb}</p>
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase  transition-colors"
        style={{ backgroundColor: accent, color: "#050505" }}
      >
        {icon} {cta}
      </span>
    </Link>
  );
}
